import type { Vec2 } from '../types';

/** Euclidean length of a 2D vector (|v|). One definition, used everywhere for determinism. */
export function vecLen(v: Vec2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

/** Internal alias kept for readability within this module. */
const len = vecLen;

/** Signed smallest angle from heading `b` to heading `a`, in (−π, π]. */
export function angleDiff(a: number, b: number): number {
  return Math.atan2(Math.sin(a - b), Math.cos(a - b));
}

/** Rotate angle `cur` toward `tgt` by at most `maxStep` radians (shortest way). */
export function slewAngle(cur: number, tgt: number, maxStep: number): number {
  const d = angleDiff(tgt, cur);
  if (Math.abs(d) <= maxStep) return tgt;
  return cur + Math.sign(d) * maxStep;
}

/**
 * Unit vector in the direction of v — division by |v| (normative, §18.2 T-MATH-001).
 * The zero vector maps to {0,0} (no division by zero).
 */
export function vecNorm(v: Vec2): Vec2 {
  const l = len(v);
  if (l === 0) return { x: 0, y: 0 };
  return { x: v.x / l, y: v.y / l };
}

/**
 * Clamp the magnitude of v to at most maxMag; shorter vectors are returned unchanged
 * (copied). Used by §5.2 to accel-limit the velocity delta. Returns {0,0} for the zero vector.
 */
export function clampMag(v: Vec2, maxMag: number): Vec2 {
  const l = len(v);
  if (l <= maxMag || l === 0) return { x: v.x, y: v.y };
  const s = maxMag / l;
  return { x: v.x * s, y: v.y * s };
}

/**
 * Unsigned angle in [0, π] between two vectors (§7.1 behind01, §7.4 momentum01).
 * atan2(|cross|, dot) is numerically robust near 0 and π (more so than acos(dot/(|a||b|))).
 */
export function angleBetween(a: Vec2, b: Vec2): number {
  const dot = a.x * b.x + a.y * b.y;
  const cross = a.x * b.y - a.y * b.x;
  return Math.atan2(Math.abs(cross), dot);
}

/** Rotate v counter-clockwise by theta radians. */
export function rotate(v: Vec2, theta: number): Vec2 {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return { x: v.x * c - v.y * s, y: v.x * s + v.y * c };
}
